/**
 * DraftTicker — Live Draft's running pick strip (Stage 2.2).
 *
 * One small tab per pick made, newest at the right. Draggable/swipeable
 * (native scroll + a mouse-drag handler for desktop trackpads that don't
 * generate wheel-scroll deltas), keyboard-navigable, and auto-scrolls to
 * a newly-arrived pick ONLY when the user was already parked near the
 * right edge — otherwise it never yanks someone back mid-scroll-through-
 * history. New tabs slide in once, not on every re-render.
 */

import { useEffect, useRef, useState } from 'react'
import { V2, PosChip, solidChip } from '../../components/JaiV2'
import { JAI_FONTS } from '../../components/JaiBrand'
import TeamChip from '../../components/TeamChip'

const VERDICT_COLOR = { good: V2.success, okay: V2.warning, bad: V2.error }
const VERDICT_LABEL = { good: 'Good Pick', okay: 'Okay Pick', bad: 'Bad Pick' }
const NEAR_EDGE_PX = 120

function TickerTab({ pick, sport, isNew }) {
  const verdictChip = solidChip(VERDICT_COLOR[pick.verdict] || V2.mute)
  return (
    <div
      style={{
        flexShrink: 0, width: 132, background: 'white', border: `1px solid ${V2.hairSoft}`,
        borderRadius: 12, padding: '10px 11px', marginRight: 8,
        animation: isNew ? 'jai-ticker-slide-in 260ms ease-out' : 'none',
        outline: pick.isMyPick ? `2px solid ${V2.blue}` : 'none',
        outlineOffset: -2,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 4 }}>
        #{pick.overallPick}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: V2.navy, marginBottom: 6, lineHeight: 1.25, minHeight: 32, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {pick.name}
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 7 }}>
        <PosChip>{pick.position}</PosChip>
        <TeamChip abbr={pick.team} sport={sport} />
      </div>
      <div style={{
        fontSize: 9.5, fontWeight: 800, textAlign: 'center', borderRadius: 6, padding: '2px 0',
        ...verdictChip,
        letterSpacing: '0.03em', textTransform: 'uppercase',
      }}>
        {VERDICT_LABEL[pick.verdict] || pick.verdict}
      </div>
    </div>
  )
}

export default function DraftTicker({ picks = [], sport = 'nba' }) {
  const scrollRef = useRef(null)
  const dragRef = useRef(null)
  // Cursor style needs to re-render on change, so it's state; the drag
  // tracking itself (startX/startScroll, read on every mousemove) stays in
  // a ref so those high-frequency updates don't trigger re-renders.
  const [isDragging, setIsDragging] = useState(false)

  // Which picks are new since the last render — computed during render by
  // comparing against a ref of the previous keys, not via an effect. A CSS
  // keyframe animation only plays once per style-value change, so there's
  // nothing to "clear" afterwards: once it finishes, the element just sits
  // at its normal (unanimated) resting style, which is already the
  // keyframe's end state.
  const prevKeysRef = useRef(new Set())
  const newKeys = new Set()
  picks.forEach((p) => { if (!prevKeysRef.current.has(p.overallPick)) newKeys.add(p.overallPick) })
  const grew = picks.length > prevKeysRef.current.size
  prevKeysRef.current = new Set(picks.map((p) => p.overallPick))

  // Auto-scroll to the newest pick only when already near the right edge —
  // an imperative DOM effect (reading/writing scrollLeft), the legitimate
  // use of useEffect here.
  useEffect(() => {
    const el = scrollRef.current
    if (!grew || !el) return
    const wasNearEdge = el.scrollWidth - el.scrollLeft - el.clientWidth < NEAR_EDGE_PX + 132 // +1 tab width, pre-append
    if (wasNearEdge) requestAnimationFrame(() => { el.scrollLeft = el.scrollWidth })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks.length])

  const onKeyDown = (e) => {
    const el = scrollRef.current
    if (!el) return
    if (e.key === 'ArrowRight') { el.scrollBy({ left: 160, behavior: 'smooth' }); e.preventDefault() }
    if (e.key === 'ArrowLeft') { el.scrollBy({ left: -160, behavior: 'smooth' }); e.preventDefault() }
  }

  // Mouse-drag scroll for trackpads/desktop — native scroll already covers
  // touch and wheel.
  const onMouseDown = (e) => {
    dragRef.current = { startX: e.clientX, startScroll: scrollRef.current.scrollLeft, dragging: true }
    setIsDragging(true)
  }
  const onMouseMove = (e) => {
    if (!dragRef.current?.dragging) return
    const dx = e.clientX - dragRef.current.startX
    scrollRef.current.scrollLeft = dragRef.current.startScroll - dx
  }
  const endDrag = () => {
    if (dragRef.current) dragRef.current.dragging = false
    setIsDragging(false)
  }

  if (!picks.length) {
    return (
      <div style={{ background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 12, padding: '14px 18px', fontSize: 12.5, color: V2.mute, marginBottom: 20 }}>
        No picks yet — the first pick will appear here.
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <style>{`
        @keyframes jai-ticker-slide-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        ref={scrollRef}
        role="list"
        aria-label="Draft picks"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        style={{
          display: 'flex', overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4,
          cursor: isDragging ? 'grabbing' : 'grab', outline: 'none',
          scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch',
        }}
      >
        {picks.map((pick) => (
          <TickerTab key={pick.overallPick} pick={pick} sport={sport} isNew={newKeys.has(pick.overallPick)} />
        ))}
      </div>
    </div>
  )
}
